/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description SelectedPayers
 */
import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { Col, Divider, Flex, Row, Select } from 'antd';

import { ACTIVE_PAYERS_CODES } from 'utils/eligibility/eligibilityRequestForm';
import { ELIGIBILITY_UNAVAILABLE_PAYER_LABEL } from 'app-constants/eligibility';
import Button from 'components/widgets/button';
import { CLAIMS_UNAVAILABLE_PAYER_LABEL } from 'app-constants/claims';

export interface IProps {
	value: string;
	forEligibility: boolean;
	forClaimStatus: boolean;
	selectedPayers: Payers.ISelectedPayer[];
	onChange: (value: string) => void;
}

/**
 * SelectedPayers
 * @param {IProps} props
 * @returns {React.FC} SelectedPayers
 */
const SelectedPayers: React.FC<IProps> = ({ value, forEligibility, forClaimStatus, selectedPayers, onChange }) => {
	const [payer, setPayer] = useState(value);

	const options = useMemo(() => {
		const availablePayers = _.filter(
			selectedPayers,
			({ isEligibility, isClaimStatus, status }) =>
				_.includes(ACTIVE_PAYERS_CODES, status) && ((forEligibility && isEligibility) || (forClaimStatus && isClaimStatus)),
		);
		const unAvailablePayers = _.filter(
			selectedPayers,
			({ isEligibility, isClaimStatus, status }) =>
				_.includes(ACTIVE_PAYERS_CODES, status) && ((forEligibility && !isEligibility) || (forClaimStatus && !isClaimStatus)),
		);
		const availablePayerOptions = _.map(availablePayers, ({ id, name }) => ({
			value: id,
			label: name,
			searchLabel: name,
		}));
		const unAvailablePayerOptions = [];
		if (!_.isEmpty(unAvailablePayers)) {
			unAvailablePayerOptions.push({
				label: forEligibility ? ELIGIBILITY_UNAVAILABLE_PAYER_LABEL : CLAIMS_UNAVAILABLE_PAYER_LABEL,
				options: _.map(unAvailablePayers, ({ id, name }) => ({
					value: id,
					disabled: true,
					searchLabel: name,
					label: (
						<Row gutter={16}>
							<Col span={24} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{name}
							</Col>
						</Row>
					),
				})),
			});
		}
		return [...availablePayerOptions, ...unAvailablePayerOptions];
	}, [selectedPayers]);

	useEffect(() => {
		if (value !== payer) {
			setPayer(value);
		}
	}, [value]);

	const sendDataToProps = (newPayer: string) => {
		setPayer(newPayer);
		onChange(newPayer);
	};

	const dropdownRender = (menu: React.ReactElement) => (
		<>
			{menu}
			<Divider style={{ margin: '4px 0' }} />
			<Flex justify='center' style={{ padding: '4px 8px' }}>
				<Button.AddPayerModal />
			</Flex>
		</>
	);

	return (
		<Select
			showSearch
			placeholder={'Select Payer'}
			value={payer || undefined}
			optionFilterProp='searchLabel'
			onChange={sendDataToProps}
			options={options || []}
			popupRender={forEligibility ? dropdownRender : undefined}
		/>
	);
};

export default SelectedPayers;
